import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { DPNotificationSettings } from '../models/DPNotificationSettings';
import { DPUserDTO } from '../models/DPUserDto';
import { DPSimpleAPIResponse } from '../models/DPSimpleAPIResponse';
import { DPUpdateUserMethod } from '../models/enums/DPUpdateUserMethod';
import { AuthService } from './auth.service';
import {
  HttpOptions,
  UserApiServiceInterface,
} from './user-api.service.interface';
import { DPLoginMethodDto } from '../models/DPLoginMethodDto';

/**
 * This service provides methods to update user details and various user settings.
 */
@Injectable({
  providedIn: 'root',
})
export class UserApiService implements UserApiServiceInterface {
  constructor(private auth: AuthService, private http: HttpClient) {}

  /**
   * Update user information
   *
   * @param method Endpoints to update user info
   * @param userInfo Updated user data
   * @returns
   */
  public updateUserInfo(method: DPUpdateUserMethod, userInfo: DPUserDTO) {
    return this.patch<DPSimpleAPIResponse>(`/user/${method}`, userInfo);
  }

  /**
   * Update login information
   *
   * @param loginMethod Login method
   * @returns
   */
  public updateLoginMethod(loginMethod: DPLoginMethodDto) {
    return this.patch<DPSimpleAPIResponse>(`/user/UpdateLoginMethod?method=${loginMethod.method}`, loginMethod);
  }

  /**
   * Sends a request to generate code for SMS or Email update flow.
   *
   * @param data Contains username or phone number
   * @returns
   */
  public generateCode(data: { UserName?: string; PhoneNumber?: string }) {
    return this.post<string | DPSimpleAPIResponse>(`/user/GenerateCode`, data);
  }

  /**
   * Verify codes in SMS or Email update flow.
   *
   * @param data
   * @returns
   */
  public verifyCode(data: { LongCode: string; ShortCode: string }) {
    return this.post<DPSimpleAPIResponse>(`/user/VerifyCode`, data);
  }

  public getNotificationSettings() {
    return this.get<DPNotificationSettings>('/user/GetUserNotification');
  }

  public get<T>(url: string, options?: HttpOptions) {
    return this.http.get<T>(
      environment.authBaseURL + url,
      this.getOptions(options)
    );
  }

  public post<T, S = {}>(url: string, data?: S, options?: any) {
    return this.http.post<T>(
      environment.authBaseURL + url,
      data,
      this.getOptions(options)
    );
  }

  public patch<T, S = {}>(url: string, data: S, options?: any) {
    return this.http.patch<T>(
      environment.authBaseURL + url,
      data,
      this.getOptions(options)
    );
  }

  private getOptions(custom?: HttpOptions): HttpOptions {
    return {
      ...custom,
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${this.auth.token}`,
        ...custom?.headers,
      },
    };
  }
}
